import { Map, View, Overlay } from 'ol';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OSM from 'ol/source/OSM';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat, transformExtent } from 'ol/proj';
import Style from 'ol/style/Style';
import Icon from 'ol/style/Icon';
import Text from 'ol/style/Text';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import { defaults as defaultControls } from 'ol/control/defaults';
import { RotateNorthControl } from './control.js';
import { CCS_TYPES } from './stations.js';
const isCCS = type => CCS_TYPES.has(type);
import { loadGE } from './api.js';
import { minPoints, minLoad, lastZoom, lastExtent, filterProblems, setMinPoints, setMinLoad, setLastZoom, setLastExtent, setFilterProblems } from './state.js';

const iconStyle = new Style({
  image: new Icon({
    anchor: [0.5, 200],
    anchorXUnits: 'fraction',
    anchorYUnits: 'pixels',
    src: 'https://www.geocodezip.net/mapIcons/google-maps-gps-icon-14.jpg',
    scale: 0.1,
  }),
});

const labelStyle = new Style({
  text: new Text({
    font: '24px Calibri,sans-serif',
    overflow: true,
    fill: new Fill({ color: '#000' }),
    stroke: new Stroke({ color: '#fff', width: 3 }),
    offsetY: -22,
  }),
});

const featureStyle = [iconStyle, labelStyle];

export const featureSource = new VectorSource();

const featureLayer = new VectorLayer({
  source: featureSource,
  style: function (feature) {
    labelStyle.getText().setText(feature.properties.cnt);
    return featureStyle;
  },
});

function onFilterChange(reload) {
  setLastZoom(999);
  reload();
}

export function createMap() {
  const map = new Map({
    controls: defaultControls().extend([
      new RotateNorthControl({
        onMinPoints:      (v) => { setMinPoints(v);      onFilterChange(loadForView); },
        onMinLoad:        (v) => { setMinLoad(v);        onFilterChange(loadForView); },
        onFilterProblems: (v) => { setFilterProblems(v); onFilterChange(loadForView); },
        filterProblems,
      }),
    ]),
    target: 'map',
    layers: [
      new TileLayer({ source: new OSM() }),
      featureLayer,
    ],
    view: new View({
      center: fromLonLat([8.3, 49.3]),
      zoom: 8,
    }),
  });

  mapRef = map;

  // --- popup overlay ---
  const popupEl = document.createElement('div');
  popupEl.className = 'station-popup station-popup--hidden';
  document.getElementById('map').appendChild(popupEl);

  const popup = new Overlay({
    element: popupEl,
    positioning: 'bottom-center',
    offset: [0, -18],
    stopEvent: true,
  });
  map.addOverlay(popup);

  const closePopup = () => {
    popupEl.classList.add('station-popup--hidden');
    popup.setPosition(undefined);
  };

  map.getView().on('change', () => { closePopup(); loadForView(); });

  function countCCS(chargepoints) {
    if (!chargepoints) return { total: 0, free: null };
    let total = 0, free = 0, hasFree = false;
    for (const pnt of chargepoints) {
      if (!isCCS(pnt.type) || pnt.power < minLoad) continue;
      total += pnt.count || 0;
      if (pnt.available != null) { free += pnt.available; hasFree = true; }
    }
    return { total, free: hasFree ? free : null };
  }

  function showPopup(locs, coordinate) {
    const rows = locs.map(loc => {
      const { total, free } = countCCS(loc.chargepoints);
      const freeStr = free != null ? ` &bull; <span class="${free > 0 ? 'popup-free' : 'popup-busy'}">${free} free</span>` : '';
      const name = loc.network || loc.name || '—';
      const nameEl = loc.url
        ? `<a class="popup-name popup-name--link" href="${loc.url}" target="_blank">${name} &#8599;</a>`
        : `<span class="popup-name">${name}</span>`;
      return `<div class="popup-row">${nameEl}<span class="popup-count">${total} CCS${freeStr}</span></div>`;
    }).join('');

    popupEl.innerHTML = `<button class="popup-close">&times;</button>${rows}`;
    popupEl.querySelector('.popup-close').addEventListener('click', closePopup);
    popupEl.querySelectorAll('.popup-name--link').forEach(a => a.addEventListener('click', e => e.stopPropagation()));

    popup.setPosition(coordinate);
    popupEl.classList.remove('station-popup--hidden');
  }

  map.on('click', (e) => {
    const feature = map.forEachFeatureAtPixel(e.pixel, f => f, { layerFilter: l => l === featureLayer });
    if (!feature) { closePopup(); return; }
    const locs = feature.properties?.locs;
    if (locs?.length) showPopup(locs, feature.getGeometry().getCoordinates());
  });

  map.on('pointermove', (e) => {
    const hit = map.hasFeatureAtPixel(e.pixel, { layerFilter: l => l === featureLayer });
    map.getTargetElement().style.cursor = hit ? 'pointer' : '';
  });

  loadForView();

  return map;
}

let mapRef;

export function initMap() {
  return createMap();
}

export function triggerLoad() {
  setLastZoom(999);
  loadForView();
}

function loadForView() {
  const view = mapRef.getView();
  const zoom = view.getZoom();
  const extent = view.calculateExtent(mapRef.getSize());

  if (zoom !== lastZoom || extent.toString() !== lastExtent.toString()) {
    setLastZoom(zoom);
    setLastExtent(extent.slice());
    const lonLatExtent = transformExtent(extent, 'EPSG:3857', 'EPSG:4326');
    loadGE(lonLatExtent, zoom, minLoad, minPoints, extent, filterProblems, (features) => {
      featureSource.clear();
      featureSource.addFeatures(features);
    });
  }
}
